import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Divider } from "@material-ui/core";
import api from "../../../../../../services/api";
import SyncLoader from "react-spinners/SyncLoader";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import * as Dialog from "@radix-ui/react-dialog";
import { useRoutes } from "../../../../../../hooks/routes";

import IListTemplateApiProps from "./dtos/IListTemplateApiProps";
import ICreatorAndEditorProps from "./dtos/ICreatorAndEditorProps";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import Button from "../../../../../../shared/components/Button";
import DialogModal from "../../../../../../shared/components/DialogModal";

import { Notify } from "../../../../../../shared/utils";

import {
  Container,
  Title,
  Subtitle,
  ContainerLoading,
  Img,
  ContainerButton,
} from "./styles";

const ViewTemplate: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [creator, setCreator] = useState<ICreatorAndEditorProps>({ name: "" });
  const [lastEditor, setLastEditor] = useState<ICreatorAndEditorProps>({
    name: "",
  });
  const [templateData, setTemplateData] = useState<IListTemplateApiProps>({
    bits_template: {
      id: "",
      bits_template_category_id: "",
      creator_id: "",
      last_editor_id: "",
      template: {},
      title: "",
      description: "",
      key_words: "",
      usage_counter: 0,
      thumbnail: "",
      template_type: "",
      is_edit: false,
      is_active: false,
      created_at: new Date(),
      updated_at: new Date(),
      thumbnail_url: "",
    },
    bits_template_categories: {
      id: "",
      creator_id: "",
      last_editor_id: "",
      title: "",
      created_at: new Date(),
      updated_at: new Date(),
    },
  });

  const location = useLocation();
  const dataLocation: any = location.state;

  const { data: dataUseAuth } = useAuth();

  const { getUrl } = useRoutes();

  const userPermissionReadUser = dataUseAuth.admin_user_permissions.read_users;

  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_templates;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_templates;

  const { template_id } = useParams();

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/view-template/:template_id");
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const listTemplateSpecific = await api.get(
          `/bits-templates/${template_id}`
        );

        if (userPermissionReadUser) {
          const userCreator = await api.get(
            `/profile/${listTemplateSpecific.data.bits_template.creator_id}`
          );

          const userLastEditor = await api.get(
            `/profile/${listTemplateSpecific.data.bits_template.last_editor_id}`
          );

          setCreator({
            name: `${userCreator.data.people.first_name} ${userCreator.data.people.last_name}`,
          });
          setLastEditor({
            name: `${userLastEditor.data.people.first_name} ${userLastEditor.data.people.last_name}`,
          });
        } else {
          setCreator({
            name: "Anônimo",
          });
          setLastEditor({
            name: "Anônimo",
          });
        }

        setTemplateData(listTemplateSpecific.data);
        setLoading(false);
      } catch (error: any) {
        console.log(error);
      }
    })();
  }, [template_id, userPermissionReadUser]);

  const handleDeleteTemplate = useCallback(
    async (id: string) => {
      await api.delete(`/bits-templates/${id}`);
      Notify({ text: "Template deletado com sucesso", type: "success" });
      navigate("/templates", {
        state: { params: dataLocation?.params },
      });
    },
    [navigate, dataLocation]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Visualizar template" />
      <MasterMain>
        {loading && (
          <ContainerLoading>
            <SyncLoader loading={loading} color="#663399" size={10} />
          </ContainerLoading>
        )}

        {!loading && (
          <Container>
            <Title>Thumbnail:</Title>
            <Img src={templateData.bits_template.thumbnail_url} />

            <Divider style={{ marginBottom: 20, marginTop: 20 }} />

            <Title>ID:</Title>
            <Subtitle>
              {templateData.bits_template.id
                ? templateData.bits_template.id
                : "null"}
            </Subtitle>

            <Title>Título:</Title>
            <Subtitle>
              {templateData.bits_template.title
                ? templateData.bits_template.title
                : "null"}
            </Subtitle>

            <Title>Descrição:</Title>
            <Subtitle>
              {templateData.bits_template.description
                ? templateData.bits_template.description
                : "null"}
            </Subtitle>

            <Title>Palavras chave:</Title>
            <Subtitle>
              {templateData.bits_template.key_words
                ? templateData.bits_template.key_words
                : "null"}
            </Subtitle>

            <Title>Número de vezes usada:</Title>
            <Subtitle>{templateData.bits_template.usage_counter}</Subtitle>

            <Title>Tipo do template:</Title>
            <Subtitle>{templateData.bits_template.template_type}</Subtitle>

            <Title>Está ativo?</Title>
            <Subtitle>
              {templateData.bits_template.is_active ? "Sim" : "Não"}
            </Subtitle>

            <Title>Está editável?</Title>
            <Subtitle>
              {templateData.bits_template.is_edit ? "Sim" : "Não"}
            </Subtitle>

            <Title>Criador:</Title>
            <Subtitle>{creator.name}</Subtitle>

            <Title>Último a editar:</Title>
            <Subtitle>{lastEditor.name}</Subtitle>

            <Title>Criado em:</Title>
            <Subtitle>
              {`${new Date(templateData.bits_template.created_at)}`
                ? `${format(
                    new Date(templateData.bits_template.created_at),
                    "dd/MM/yyyy HH:mm"
                  )}`
                : "null"}
            </Subtitle>

            <Title>Última atualização:</Title>
            <Subtitle>
              {`${new Date(templateData.bits_template.updated_at)}`
                ? `${format(
                    new Date(templateData.bits_template.updated_at),
                    "dd/MM/yyyy HH:mm"
                  )}`
                : "null"}
            </Subtitle>

            <Divider style={{ marginBottom: 20 }} />

            <Title>Categorias:</Title>
            <Subtitle style={{ marginBottom: 0, marginTop: 5 }}>
              {templateData.bits_template_categories.title}
            </Subtitle>

            <Divider style={{ marginBottom: 20, marginTop: 20 }} />

            <ContainerButton>
              <Button
                style={{ width: 100 }}
                onClick={() =>
                  navigate("/templates", {
                    state: { params: dataLocation?.params },
                  })
                }
              >
                Voltar
              </Button>

              <Button
                style={{ width: 100, marginLeft: 20 }}
                onClick={() =>
                  navigate(`/edit-template/${template_id}`, {
                    state: { params: dataLocation?.params },
                  })
                }
                disabled={!userPermissionUpdate}
              >
                Editar
              </Button>

              <Dialog.Root>
                <Dialog.Trigger asChild>
                  <Button
                    style={{ width: 100, marginLeft: 20 }}
                    disabled={!userPermissionDelete}
                  >
                    Excluir
                  </Button>
                </Dialog.Trigger>

                <DialogModal
                  title="Deletar template"
                  type="delete"
                  subtitle={templateData.bits_template.title}
                  callback={() =>
                    handleDeleteTemplate(templateData.bits_template.id)
                  }
                />
              </Dialog.Root>
            </ContainerButton>
          </Container>
        )}
      </MasterMain>
    </MasterContainer>
  );
};

export default ViewTemplate;
